import { useEffect } from 'react';
import { Navigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ROUTES } from '#/config/routes';
import { isAuthEnabled } from '#/config/auth';
import { Button } from '#/modules/commonComponents/button';
import { fetchMe } from '#/modules/infra/redux/infraSlice';
import { getAccess } from '#/utils/auth/tokenStorage';

/**
 * 保護 /admin/* — 未登入導向 /login；有 token 必等 GET /auth/me。
 */
const AuthGuard = ({ children }) => {
  const dispatch = useDispatch();
  const access = useSelector((state) => state.infra.access);
  const permissionsReady = useSelector((state) => state.infra.permissionsReady);
  const isMeLoading = useSelector((state) => state.infra.isMeLoading);
  const meError = useSelector((state) => state.infra.meError);

  const isAuthenticated = Boolean(access || getAccess());

  useEffect(() => {
    if (!isAuthEnabled) {
      return;
    }
    if (!isAuthenticated) {
      return;
    }
    if (permissionsReady || isMeLoading) {
      return;
    }
    dispatch(fetchMe());
  }, [dispatch, isAuthenticated, permissionsReady, isMeLoading]);

  if (!isAuthEnabled) {
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!permissionsReady) {
    if (meError) {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-muted-foreground">
            {meError.message || '載入使用者權限失敗'}
          </p>
          <Button type="button" onClick={() => dispatch(fetchMe())}>
            重試
          </Button>
        </div>
      );
    }

    return (
      <div className="flex min-h-[40vh] items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">載入中…</p>
      </div>
    );
  }

  return children;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
